import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ETypeOfNotification, Modal, setNotification } from 'medere-ui-framework';
import { UserSessionDTO } from '../../../dto/locationcash/LocationCashDTO';
import { takeOverLocation } from '../../../services/LocationCashController';
import { parseApiError } from '../../../services/apiError';
import { hasDiscrepancy, isValidDeclaredAmount } from '../utils';
import useCashDiscrepancyConfirmation from '../useCashDiscrepancyConfirmation';
import CashDiscrepancyConfirm from './CashDiscrepancyConfirm';

interface TakeOverModalProps {
    visible: boolean;
    hospitalLocationId: number;
    locationDescription: string;
    previousUsername: string;
    hasCashRegister: boolean;
    onHide: () => void;
    onSuccess: (session: UserSessionDTO) => void;
}

export default function TakeOverModal({
    visible,
    hospitalLocationId,
    locationDescription,
    previousUsername,
    hasCashRegister,
    onHide,
    onSuccess,
}: TakeOverModalProps) {
    const dispatch = useDispatch();
    const [declaredAmount, setDeclaredAmount] = useState<number | null>(null);
    const [observations, setObservations] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const discrepancy = useCashDiscrepancyConfirmation();

    useEffect(() => {
        if (!visible) return;
        setDeclaredAmount(null);
        setObservations('');
        setConfirmed(false);
        discrepancy.reset();
    }, [visible, hospitalLocationId]);

    const canSubmit = confirmed && (!hasCashRegister || isValidDeclaredAmount(declaredAmount));

    // El checkbox repite lo que se declara: al revisar el monto hay que volver a confirmarlo
    const handleReview = () => {
        discrepancy.review();
        setConfirmed(false);
    };

    // Con la confirmación a la vista, ✕ / Esc equivalen a "Revisar el monto" y no cierran la operación
    const handleVisibleChange = (value: boolean) => {
        if (value || loading) return;
        if (discrepancy.pending) handleReview();
        else onHide();
    };

    const handleSubmit = async (acceptDiscrepancy = false) => {
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { session, differenceAmount } = await takeOverLocation({
                hospitalLocationId,
                declaredAmount: hasCashRegister ? declaredAmount : null,
                incomingObservations: observations.trim() || undefined,
                acceptDiscrepancy,
            });
            dispatch(setNotification({
                title: 'Puesto tomado',
                desc: !hasCashRegister
                    ? 'Se cerró la sesión anterior y se inició la nueva sesión.'
                    : hasDiscrepancy(differenceAmount)
                        ? 'Se cerró la sesión anterior y se registró el ingreso de caja con una diferencia pendiente de justificación.'
                        : 'Se cerró la sesión anterior y se registró el ingreso de caja.',
                icon: 'pi-check-circle',
                typeOfNotification: ETypeOfNotification.INFO,
            }));
            onSuccess(session);
        } catch (error) {
            const apiError = parseApiError(error);
            if (discrepancy.handleRejection(apiError)) return;
            dispatch(setNotification({
                title: 'No se pudo tomar el puesto',
                desc: apiError.message,
                icon: 'pi-times',
                typeOfNotification: ETypeOfNotification.ERROR,
            }));
            onHide();
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            visible={visible}
            setVisible={handleVisibleChange}
            header={`Tomar puesto — ${locationDescription}`}
            width="w-medium"
            closable={!loading}
        >
            {discrepancy.pending ? (
                <CashDiscrepancyConfirm
                    confirming={loading}
                    onReview={handleReview}
                    onConfirm={() => handleSubmit(true)}
                />
            ) : (
                <div className="flex flex-column gap-3">
                    <div className="handoff-receipt">
                        <h4 className="handoff-receipt-title">Toma forzada del puesto</h4>
                        <p className="m-0" style={{ color: '#5A6676', fontSize: 13 }}>
                            El puesto está asignado a <strong>{previousUsername}</strong>. Al confirmar,
                            su sesión se cerrará automáticamente y comenzarás una nueva sesión a tu nombre.
                        </p>
                    </div>

                    {hasCashRegister && (
                        <div className="flex flex-column gap-1">
                            <label htmlFor="takeover-declared-amount" className="font-medium" style={{ fontSize: 13 }}>
                                Monto encontrado en caja *
                            </label>
                            <InputNumber
                                inputId="takeover-declared-amount"
                                inputRef={discrepancy.declaredAmountRef}
                                value={declaredAmount}
                                onValueChange={e => setDeclaredAmount(e.value ?? null)}
                                mode="currency"
                                currency="ARS"
                                locale="es-AR"
                                min={0}
                                disabled={loading}
                                placeholder="Ingrese el monto contabilizado"
                            />
                            <small style={{ color: '#6B7280' }}>
                                El sistema registrará el saldo correspondiente y dejará asentada cualquier diferencia.
                            </small>
                        </div>
                    )}

                    <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ fontSize: 13 }}>Observaciones (opcional)</label>
                        <InputTextarea
                            value={observations}
                            onChange={e => setObservations(e.target.value)}
                            rows={3}
                            autoResize
                            disabled={loading}
                            placeholder="Por ejemplo: el usuario anterior se retiró sin cerrar sesión..."
                        />
                    </div>

                    <div className="flex align-items-center gap-2">
                        <Checkbox
                            inputId="takeover-confirm"
                            checked={confirmed}
                            onChange={e => setConfirmed(!!e.checked)}
                            disabled={loading || (hasCashRegister && !isValidDeclaredAmount(declaredAmount))}
                        />
                        <label htmlFor="takeover-confirm" className="cursor-pointer" style={{ fontSize: 13 }}>
                            Confirmo que estoy tomando este puesto y verificando su estado actual.
                        </label>
                    </div>

                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="buttonMain3"
                            onClick={onHide}
                            disabled={loading}
                        />
                        <Button
                            label="Tomar puesto"
                            icon="pi pi-user-edit"
                            className="buttonMain2"
                            onClick={() => handleSubmit()}
                            loading={loading}
                            disabled={!canSubmit || loading}
                        />
                    </div>
                </div>
            )}
        </Modal>
    );
}
